// Celdas compartidas de las tablas por modelo: el modelo con su chevron y la subfila de variante.
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { variantLabel } from '@/shared/lib/format/capacity';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import type { ModelGroup } from '../lib/modelGroups';

/** Nombre del modelo con el chevron que despliega sus variantes. */
export function ModelNameCell({
    group,
    expanded,
    onToggle,
}: {
    group: ModelGroup;
    expanded: boolean;
    onToggle: () => void;
}) {
    const name = [group.brand, group.model].filter((part) => part.trim() !== '').join(' ');
    return (
        <span className="flex min-w-0 items-center gap-1">
            <button
                type="button"
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Ocultar' : 'Ver'} variantes de ${name}`}
                onClick={(event) => {
                    event.stopPropagation();
                    onToggle();
                }}
                className="flex size-6 flex-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <ChevronRight
                    className={cn('size-3.5 transition-transform', expanded && 'rotate-90')}
                    aria-hidden
                />
            </button>
            <span className="flex min-w-0 flex-col leading-tight">
                <span data-row-name className="truncate font-semibold" title={group.model}>
                    {group.model}
                </span>
                <span className="truncate text-[10px] text-muted-foreground">{group.brand}</span>
            </span>
        </span>
    );
}

/** Subfila de una variante: la ficha «8 GB RAM · 256 GB · Negro» y su SKU. */
export function VariantNameCell({ item }: { item: CatalogItem }) {
    const label = variantLabel(item);
    return (
        <span className="flex min-w-0 items-center gap-2 pl-7">
            <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-medium" title={label}>
                    {label || item.sku}
                </span>
                <span
                    data-row-name
                    className="truncate font-mono text-[10px] text-muted-foreground"
                >
                    {item.sku}
                </span>
            </span>
        </span>
    );
}

/** «Vender Samsung Galaxy A16 8 + 256 GB · Negro», el nombre accesible del botón de la variante. */
export function sellLabel(item: CatalogItem): string {
    return ['Vender', item.brand, item.model, variantLabel(item)]
        .filter((part) => part.trim() !== '')
        .join(' ');
}

/** Financieras de todas las variantes del modelo, sin repetir. */
export function groupFinanciers(group: ModelGroup): string[] {
    return [...new Set(group.variants.flatMap((variant) => variant.eligibleFinanciers))];
}

/** Fondo de las subfilas de variante, para que se lean como parte de su modelo. */
export const VARIANT_ROW_CLASS = 'bg-muted/40';
